import axios from 'axios';
import { env } from '@/lib/env';
import { challengeFromVerifier, generateVerifier, randomState } from './pkce';
import { useAuthStore } from './store';

type DialogMessage = {
  type: 'auth-success' | 'auth-error';
  code?: string;
  state?: string;
  error?: string;
};

const buildAuthorizeUrl = async (verifier: string, state: string) => {
  const challenge = await challengeFromVerifier(verifier);
  const params = new URLSearchParams({
    response_type: 'code',
    client_id: env.cognitoClientId,
    redirect_uri: env.redirectUri,
    scope: env.scope,
    code_challenge_method: 'S256',
    code_challenge: challenge,
    state,
  });
  return `${env.cognitoDomain}/oauth2/authorize?${params.toString()}`;
};

const exchangeCode = async (code: string, verifier: string) => {
  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: env.cognitoClientId,
    code,
    redirect_uri: env.redirectUri,
    code_verifier: verifier,
  });
  const res = await axios.post(`${env.cognitoDomain}/oauth2/token`, body, {
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
  });
  return res.data as {
    access_token: string;
    refresh_token?: string;
    id_token?: string;
    expires_in: number;
    token_type: string;
  };
};

const isOfficeDialogAvailable = (): boolean => {
  return (
    typeof Office !== 'undefined' &&
    typeof Office.context?.ui?.displayDialogAsync === 'function'
  );
};

const finishLogin = async (
  code: string,
  receivedState: string,
  expectedState: string,
  verifier: string,
) => {
  if (receivedState !== expectedState) {
    throw new Error('state_mismatch');
  }
  const tokens = await exchangeCode(code, verifier);
  useAuthStore.getState().setSession({
    accessToken: tokens.access_token,
    refreshToken: tokens.refresh_token,
    idToken: tokens.id_token,
    expiresIn: tokens.expires_in,
  });
};

const loginWithBrowserPopup = async (
  verifier: string,
  state: string,
  url: string,
): Promise<void> => {
  return new Promise((resolve, reject) => {
    const popup = window.open(
      url,
      'lex-auth',
      'width=480,height=720,menubar=no,toolbar=no,location=no',
    );
    if (!popup) {
      reject(new Error('popup_blocked'));
      return;
    }

    const handleMessage = async (event: MessageEvent) => {
      if (event.source !== popup) return;
      try {
        const data =
          typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        if (!data || typeof data !== 'object') return;
        if (data.type === 'auth-error') {
          window.removeEventListener('message', handleMessage);
          window.clearInterval(closedTimer);
          popup.close();
          reject(new Error(data.error ?? 'auth_failed'));
          return;
        }
        if (data.type !== 'auth-success' || !data.code) return;
        window.removeEventListener('message', handleMessage);
        window.clearInterval(closedTimer);
        await finishLogin(data.code, data.state, state, verifier);
        popup.close();
        resolve();
      } catch (err) {
        window.removeEventListener('message', handleMessage);
        window.clearInterval(closedTimer);
        popup.close();
        reject(err);
      }
    };

    const closedTimer = window.setInterval(() => {
      if (popup.closed) {
        window.removeEventListener('message', handleMessage);
        window.clearInterval(closedTimer);
        reject(new Error('dialog_closed'));
      }
    }, 500);

    window.addEventListener('message', handleMessage);
  });
};

const loginWithOfficeDialog = (
  verifier: string,
  state: string,
  url: string,
): Promise<void> => {
  return new Promise((resolve, reject) => {
    Office.context.ui.displayDialogAsync(
      url,
      { height: 70, width: 40, promptBeforeOpen: false },
      (result) => {
        if (result.status !== Office.AsyncResultStatus.Succeeded) {
          reject(new Error(result.error?.message ?? 'dialog_failed'));
          return;
        }
        const dialog = result.value;
        dialog.addEventHandler(
          Office.EventType.DialogMessageReceived,
          async (arg) => {
            try {
              const message = JSON.parse(
                (arg as { message: string }).message,
              ) as DialogMessage;
              if (message.type !== 'auth-success' || !message.code) {
                throw new Error(message.error ?? 'auth_failed');
              }
              await finishLogin(message.code, message.state ?? '', state, verifier);
              dialog.close();
              resolve();
            } catch (err) {
              dialog.close();
              reject(err);
            }
          },
        );
        dialog.addEventHandler(Office.EventType.DialogEventReceived, () => {
          reject(new Error('dialog_closed'));
        });
      },
    );
  });
}

export async function loginWithDialog(): Promise<void> {
  const verifier = generateVerifier();
  const state = randomState();
  const url = await buildAuthorizeUrl(verifier, state);

  return isOfficeDialogAvailable()
    ? loginWithOfficeDialog(verifier, state, url)
    : loginWithBrowserPopup(verifier, state, url);
}

export async function refreshSession(): Promise<boolean> {
  const { refreshToken } = useAuthStore.getState();
  if (!refreshToken) return false;
  try {
    const body = new URLSearchParams({
      grant_type: 'refresh_token',
      client_id: env.cognitoClientId,
      refresh_token: refreshToken,
    });
    const res = await axios.post(`${env.cognitoDomain}/oauth2/token`, body, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });
    const data = res.data as {
      access_token: string;
      id_token?: string;
      expires_in: number;
    };
    useAuthStore.getState().setSession({
      accessToken: data.access_token,
      refreshToken,
      idToken: data.id_token,
      expiresIn: data.expires_in,
    });
    return true;
  } catch {
    useAuthStore.getState().clear();
    return false;
  }
}

export function logout() {
  useAuthStore.getState().clear();
}
